import axios from 'axios';
import { customerFlowNames } from 'src/components/CustomerId';
import type { OrganizationWithLogo } from 'src/hooks/useOrganizations';
import { getCmsApiBaseUrl } from './api.config';

export const SONIO_DEMO_ORG_ID = 'org_WPjCGJOzzHBV5zNP';

export interface OrgFlow {
  flowId: string;
  flowName?: string;
}

export interface GetOrganizationsParams {
  page?: number;
  per_page?: number;
}

export interface Organization {
  id: string;
  name: string;
  display_name: string;
  customerId?: string;
}

export interface CreateOrganizationData {
  customerId: string;
  name: string;
  display_name: string;
  logo?: File;
}

export interface UpdateOrganizationData {
  display_name: string;
  logo?: File;
}

export interface GetOrganizationsResponse {
  organizations?: Organization[]; // API might return organizations array directly
  data?: Organization[]; // Or wrapped in data property
  total?: number;
  page?: number;
  per_page?: number;
}

export function extractOrgFlows(org: any): OrgFlow[] {
  if (Array.isArray(org?.flows) && org.flows.length > 0) {
    return org.flows
      .map((f: any) => ({
        flowId: String(f.flowId || f.id || f._id || ''),
        flowName: f.flowName || f.name || undefined,
      }))
      .filter((f: OrgFlow) => f.flowId);
  }
  const ids = extractFlowIds(org);
  return (ids ?? []).map(flowId => ({ flowId }));
}

export function extractFlowIds(org: any): string[] | undefined {
  if (Array.isArray(org?.flows) && org.flows.length > 0) {
    const ids = org.flows
      .map((f: any) => f.flowId || f.id || f._id || '')
      .filter(Boolean);
    if (ids.length > 0) return ids;
  }
  if (Array.isArray(org?.flowIds) && org.flowIds.length > 0) {
    return org.flowIds;
  }
  return undefined;
}

export function isSonioDemoOrganization(
  org: { id?: string; name?: string; display_name?: string } | null | undefined,
): boolean {
  if (!org) return false;
  if (org.id === SONIO_DEMO_ORG_ID) return true;
  const normalizedName = org.name?.trim().toLowerCase().replace(/_/g, ' ') ?? '';
  const normalizedDisplay =
    org.display_name?.trim().toLowerCase().replace(/_/g, ' ') ?? '';
  return normalizedName === 'sonio demo' || normalizedDisplay === 'sonio demo';
}

export function buildFlowNameLookup(
  organizations: Array<{ flows?: OrgFlow[] }>,
): Record<string, string> {
  const lookup: Record<string, string> = {
    ...(customerFlowNames as Record<string, string>),
  };
  for (const org of organizations) {
    for (const flow of org.flows ?? []) {
      if (flow.flowId && flow.flowName) {
        lookup[flow.flowId] = flow.flowName;
      }
    }
  }
  return lookup;
}

export function normalizeOrganizationRecord(org: any) {
  const flows = extractOrgFlows(org);
  const flowIds = flows.map(f => f.flowId);
  return {
    id: org.id || org._id || org.organization_id || '',
    name: org.name || '',
    display_name: org.display_name || org.displayName || org.name || '',
    customerId: org.customerId || org.customer_id,
    flowIds: flowIds.length > 0 ? flowIds : extractFlowIds(org),
    flows: flows.length > 0 ? flows : undefined,
    branding: org.branding,
  };
}

/** Keep orgs that have customerId per profile; fall back to org record customerId. */
export function filterOrganizationsWithCustomerId<
  T extends { id: string; customerId?: string },
>(orgs: T[], profileAllowedIds: Set<string> | null): T[] {
  return orgs.filter(org => {
    if (profileAllowedIds) return profileAllowedIds.has(org.id);
    return Boolean(org.customerId?.trim());
  });
}

export function mapApiOrganizations(
  orgsArray: unknown[],
): OrganizationWithLogo[] {
  if (!Array.isArray(orgsArray)) return [];
  return orgsArray.map((org: any) => {
    const orgId = org.id || org._id || org.organization_id || '';
    return {
      id: orgId,
      name: org.name || '',
      display_name: org.display_name || org.displayName || org.name || '',
      label: org.display_name || org.displayName || org.name || '',
      value: (org.name || '').toLowerCase().replace(/\s+/g, '-'),
      logo: org.branding?.logo_url,
      customerId: org.customerId || org.customer_id,
      flowIds: extractFlowIds(org),
      branding: org.branding,
    };
  });
}

/**
 * Get organizations for a user (organizations the user has access to).
 * Uses same endpoint as organization slice: GET api/v1/user/organizations/:userId
 * Returns array with id, name, display_name, branding, customerId, flowIds.
 */
export const getUserOrganizations = async (
  userId: string,
  token: string,
  liveData?: boolean,
): Promise<GetOrganizationsResponse> => {
  const url = `${getCmsApiBaseUrl(liveData)}/user/organizations/${encodeURIComponent(userId)}`;
  const response = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  const responseData = response.data;
  const orgsArray = Array.isArray(responseData)
    ? responseData
    : (responseData?.data ?? responseData?.organizations ?? []);
  return { data: orgsArray };
};

/**
 * Get all organizations with pagination
 */
export const getAllOrganizations = async (
  params: GetOrganizationsParams = {},
  token: string,
  liveData?: boolean,
): Promise<GetOrganizationsResponse> => {
  const { page = 0, per_page = 10 } = params;

  const queryParams = new URLSearchParams({
    page: page.toString(),
    per_page: per_page.toString(),
  });

  const response = await axios.get(
    `${getCmsApiBaseUrl(liveData)}/organizations?${queryParams}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    },
  );

  // Handle different response structures
  const responseData = response.data;
  if (Array.isArray(responseData)) {
    // If response is directly an array
    return { data: responseData };
  }
  if (responseData.organizations) {
    // If response has organizations property
    return { data: responseData.organizations, ...responseData };
  }
  // Default: assume data property exists
  return responseData;
};

/**
 * Get all available organizations from singular endpoint
 * GET /api/v1/organization
 */
export const getAvailableOrganizations = async (
  token: string,
  liveData?: boolean,
): Promise<GetOrganizationsResponse> => {
  const response = await axios.get(
    `${getCmsApiBaseUrl(liveData)}/organizations?per_page=250`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    },
  );

  const responseData = response.data;
  if (Array.isArray(responseData)) {
    return { data: responseData };
  }

  const orgsArray = responseData?.data ?? responseData?.organizations ?? [];
  return { data: orgsArray, ...responseData };
};

/**
 * Create a new organization
 */
export const createOrganization = async (
  data: CreateOrganizationData,
  token: string,
  liveData?: boolean,
): Promise<Organization> => {
  // Backend expects multipart/form-data (like Postman form-data),
  // even when `logo` isn't provided. Also, don't set Content-Type manually:
  // Axios will add the correct boundary for FormData.
  const formData = new FormData();
  formData.append('customerId', data.customerId);
  formData.append('name', data.name);
  formData.append('display_name', data.display_name);
  if (data.logo) formData.append('logo', data.logo);

  const response = await axios.post(
    `${getCmsApiBaseUrl(liveData)}/organization`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  // Handle different response structures
  const responseData = response.data;

  // Log for debugging
  // eslint-disable-next-line no-console
  console.log('Create organization API response:', {
    fullResponse: responseData,
    hasId: !!responseData.id,
    has_id: !!responseData._id,
    hasRoleId: !!responseData.role_id,
    hasOrgId: !!responseData.organization_id,
  });

  // If response has data property, use it
  if (responseData.data) {
    return responseData.data;
  }

  // If response has organization property, use it
  if (responseData.organization) {
    return responseData.organization;
  }

  // Extract organization ID from various possible field names
  // IMPORTANT: Backend has a bug - it returns role_id for organizations
  const orgId =
    responseData.id ||
    responseData._id ||
    responseData.organization_id ||
    responseData.role_id; // Backend bug: returns role_id for organizations

  if (!orgId) {
    // eslint-disable-next-line no-console
    console.error(
      'No ID found in organization creation response:',
      responseData,
    );
  } else {
    // eslint-disable-next-line no-console
    console.log('Successfully extracted organization ID:', orgId);
  }

  // Construct organization object
  const org = {
    id: orgId || '', // Use empty string if no ID found
    name: responseData.name || data.name,
    display_name:
      responseData.display_name ||
      responseData.displayName ||
      responseData.description ||
      data.display_name,
    customerId:
      responseData.customerId || responseData.customer_id || data.customerId,
  };

  // eslint-disable-next-line no-console
  console.log('Created organization object:', org);

  return org;
};

/**
 * Update an organization
 */
export const updateOrganization = async (
  orgId: string,
  data: UpdateOrganizationData,
  token: string,
  liveData?: boolean,
): Promise<Organization> => {
  // Keep update consistent with create: use multipart/form-data and let Axios set boundary.
  const formData = new FormData();
  formData.append('display_name', data.display_name);
  if (data.logo) formData.append('logo', data.logo);

  const response = await axios.put(
    `${getCmsApiBaseUrl(liveData)}/organization/${orgId}`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  return response.data;
};

export interface OrganizationMember {
  user_id: string;
  name?: string;
  email?: string;
  given_name?: string;
  family_name?: string;
}

export function getOrganizationMemberLabel(member: OrganizationMember): string {
  if (member.name?.trim()) return member.name.trim();
  const full = [member.given_name, member.family_name]
    .filter(Boolean)
    .join(' ')
    .trim();
  if (full) return full;
  return member.email?.trim() || member.user_id;
}

/**
 * GET /api/v1/organizations/:authOrgId/members
 * Optional search: ?name=Rim
 */
export const getOrganizationMembers = async (
  authOrgId: string,
  token: string,
  options?: { name?: string; liveData?: boolean },
): Promise<OrganizationMember[]> => {
  const params = new URLSearchParams();
  if (options?.name?.trim()) params.set('name', options.name.trim());
  const qs = params.toString();
  const url = `${getCmsApiBaseUrl(options?.liveData)}/organizations/${encodeURIComponent(authOrgId)}/members${qs ? `?${qs}` : ''}`;
  const response = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  const data = response.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.members)) return data.members;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

/**
 * Delete an organization
 */
export const deleteOrganization = async (
  orgId: string,
  token: string,
  liveData?: boolean,
): Promise<void> => {
  const url = `${getCmsApiBaseUrl(liveData)}/organization/${orgId}`;

  // Log for debugging
  // eslint-disable-next-line no-console
  console.log('Delete organization request:', {
    endpoint: `${getCmsApiBaseUrl(liveData)}/organization`,
    orgId,
    fullUrl: url,
  });

  if (!orgId || orgId === 'undefined') {
    throw new Error('Cannot delete organization: Invalid organization ID');
  }

  await axios.delete(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
};
